import { Component } from 'react';
import PropTypes from 'prop-types';
import Button from './ui/Button';

/**
 * Error Boundary component to catch React errors and display fallback UI
 * Wraps the app to prevent white screen of death
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you might want to log to an error reporting service
    // Example: logErrorToService(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-light px-4">
          <div className="max-w-2xl w-full text-center">
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-error"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">
                Wystąpił błąd
              </h1>

              <p className="text-text-secondary mb-8">
                Przepraszamy, wystąpił nieoczekiwany błąd. Spróbuj odświeżyć stronę lub skontaktuj
                się z nami, jeśli problem będzie się powtarzał.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="mb-8 text-left bg-bg-light p-4 rounded border border-input-border">
                  <summary className="cursor-pointer font-semibold text-text-primary mb-2">
                    Szczegóły błędu (tylko w trybie deweloperskim)
                  </summary>
                  <pre className="text-xs text-text-secondary overflow-auto mt-2">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack && (
                      <>
                        {'\n\n'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="primary" onClick={this.handleReset}>
                  Spróbuj ponownie
                </Button>
                <Button variant="secondary" onClick={this.handleReload}>
                  Odśwież stronę
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.href = '/';
                  }}
                >
                  Strona główna
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
